// src/components/CreateSpace/Step2/Step2.jsx

import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  PrevNextButton,
  MessageBox,
} from "reusableComponents";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useLanguage, useModal } from "context";
import {
  fetchSpaceDataWithAuth,
  getPlaceServices,
  submitStep2,
  updatePlaceService,
  deletePlaceService,
} from "services/addSpace.services";
import { selectAuthToken } from "store/auth/selectors";
import { useSelector } from "react-redux";

import { useFormValidation } from "./hooks/useFormValidation";
import { useImageCompression } from "./hooks/useImageCompression";
import { flattenServicesAndSeminars, prepareSubmissionData } from "./utils/formDataUtils";
import { AreaSection } from "./components/AreaSection";
import {
  FreeServicesSection,
  ReservableServicesSection,
  OnsiteServicesSection,
  CustomServicesSection,
  SeminarSection,
} from "./components/ServicesSection";
import PicturesSection from "./components/PicturesSection";
import {
  EXTRA_PICTURES_OPTION_ID,
  MAX_TOTAL_PAYLOAD_MB,
} from "./step2.constants";
import { getDispositionTypes } from "helpers/getSpaceDispositionTypes";
import { convertUrlToFile } from "helpers/convertImgUrlToFile";
import { ReusableConfirmationModal } from "modals";

import mainStyles from "./styles.module.scss";

const Step2 = ({ space, onSubmit }) => {
  // Hooks
  const navigate = useNavigate();
  const { spaceId } = useParams();
  const { language } = useLanguage();
  const { t } = useTranslation("global");
  const accessToken = useSelector(selectAuthToken);

  // State
  const [serverErrors, setServerErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExtraPicturesAccess, setHasExtraPicturesAccess] = useState(false);
  const [compressingImages, setCompressingImages] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [isUpdatingService, setIsUpdatingService] = useState(false);
  const [isDeletingService, setIsDeletingService] = useState(false);
  const [formValues, setFormValues] = useState({
    area: null,
    desk: null,
    custom_services: [
      { name: "", price: "" },
      { name: "", price: "" },
    ],
    services: [],
    seminars: [],
    options: [],
    packs: [],
  });

  // Refs
  const isMounted = useRef(true);
  const imageErrorsRef = useRef({});

  // Custom hooks
  const {
    fieldErrors,
    validateAllRequiredFields,
    validateField,
    clearFieldError,
    clearAllErrors,
    requiresDispositionTypes,
    requiresWorkPlaces,
    requiresSeminarSection,
  } = useFormValidation(formValues, t);

  const {
    imageQueueRef,
    originalImagesRef,
    compressAllImages,
    calculateTotalPayloadSize,
    validateImageType,
    clearImage,
    clearAllImages,
  } = useImageCompression();

  // Update ref when imageErrors changes
  useEffect(() => {
    imageErrorsRef.current = imageErrors;
  }, [imageErrors]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
      clearAllImages();
    };
  }, [clearAllImages]);

  // Scroll to seminar section
  const scrollToSeminarSection = useCallback(() => {
    try {
      const seminarSection = document.querySelector('[data-section="seminar"]');
      if (seminarSection) {
        seminarSection.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } catch (error) {
      console.warn("Error scrolling to seminar section:", error);
    }
  }, []);

  // Scroll to first error field
  const scrollToError = useCallback(() => {
    try {
      if (fieldErrors?.seminars) {
        scrollToSeminarSection();
        return;
      }

      const firstErrorField = document.querySelector('[data-error="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      const errorFields = Object.keys(fieldErrors || {});
      if (errorFields.length > 0) {
        for (const fieldName of errorFields) {
          if (fieldName === "seminars") continue;

          const selectors = [
            `[name="${fieldName}"]`,
            `#${fieldName}`,
            `[data-field="${fieldName}"]`,
            `input[name="${fieldName}"]`,
            `textarea[name="${fieldName}"]`,
            `select[name="${fieldName}"]`,
          ];

          for (const selector of selectors) {
            const element = document.querySelector(selector);
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
              return;
            }
          }
        }
      }
    } catch (error) {
      console.warn("Error scrolling to error:", error);
    }
  }, [fieldErrors, scrollToSeminarSection]);

  // Check extra pictures access
  useEffect(() => {
    try {
      const access =
        (formValues?.packs?.length > 0) ||
        (formValues?.options || []).some(
          (option) => option?.option?.id === EXTRA_PICTURES_OPTION_ID
        );
      setHasExtraPicturesAccess(access);
    } catch (error) {
      console.warn("Error checking extra pictures access:", error);
    }
  }, [formValues.options, formValues.packs]);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, accessToken, language]);

  const fetchInitialData = async () => {
    if (!spaceId) return;

    setLoading(true);
    try {
      const [spaceData, placeServiceData] = await Promise.all([
        fetchSpaceDataWithAuth(spaceId, accessToken, language).catch((error) => {
          console.error("Failed to fetch space data:", error);
          return null;
        }),
        getPlaceServices(spaceId, accessToken, language).catch((error) => {
          console.error("Failed to fetch place services:", error);
          return null;
        }),
      ]);

      if (!isMounted.current) return;

      const transformedImages = await transformImages(spaceData?.images || []);

      const dispositionTypes = getDispositionTypes(t, spaceData);
      const dispositionValues = dispositionTypes.reduce((acc, type) => {
        acc[type.key2] = type.count || "";
        return acc;
      }, {});

      const { services, seminars } = flattenServicesAndSeminars(
        placeServiceData || {},
        spaceData || {}
      );

      // DEBUG: Log the services before filtering
      console.log('🔍 All services from flattenServicesAndSeminars:', services);
      console.log('🔍 Services with is_custom === 1:', services.filter(s => s.is_custom === 1));

      // CRITICAL FIX: Don't filter out custom services - keep them in the main services array
      // The CustomServicesSection will filter them using is_custom === 1
      const regularServices = services; // Include ALL services
      const customServicesFromAPI = services.filter((s) => s.is_custom === 1);

      console.log('📊 regularServices count:', regularServices.length);
      console.log('📊 customServicesFromAPI count:', customServicesFromAPI.length);
      console.log('📊 customServicesFromAPI details:', customServicesFromAPI.map(s => ({ name: s.name, is_custom: s.is_custom })));

      // FIX: Don't add existing custom services to custom_services
      // They are already in the main services array and will be displayed by CustomServicesSection
      const initialCustomServices = [
        { name: "", price: "" },
        { name: "", price: "" },
      ];

      setFormValues((prev) => ({
        place_id: spaceData?.id || null,
        place_style_id: spaceData?.placeStyle?.id || "16f35652-3e3f-41df-a175-abfc8275c8cf",
        ...prev,
        ...(spaceData || {}),
        services: regularServices,  // Includes ALL services including custom ones
        seminars: seminars || [],
        custom_services: initialCustomServices,  // Only empty inputs for new services
        ...dispositionValues,
        ...transformedImages,
      }));

      clearAllErrors();
      setServerErrors([]);
    } catch (error) {
      console.error("Failed to fetch space or place service data:", error);
      if (isMounted.current) {
        setServerErrors([
          {
            message: t("errors.loadDataFailed") || "Failed to load data. Please try again.",
            type: "error",
          },
        ]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const transformImages = useCallback(
    async (images) => {
      if (!Array.isArray(images)) return {};

      const updatedImages = {};

      for (let index = 0; index < images.length; index++) {
        const image = images[index];
        const imageKey = `image_${index + 1}`;
        const descriptionKey = `image_${index + 1}_description`;

        try {
          if (image?.image_url) {
            let file = await convertUrlToFile(image.image_url, `image_${index + 1}`).catch(
              (error) => {
                console.warn(`Failed to convert URL to file for image ${index + 1}:`, error);
                return null;
              }
            );

            if (file && isMounted.current) {
              originalImagesRef.current.set(imageKey, file);
              updatedImages[imageKey] = file;
              imageQueueRef.current.set(imageKey, file);
            }
          }

          updatedImages[descriptionKey] = image?.description || "";
        } catch (error) {
          console.warn(`Failed to process image ${index + 1}:`, error);
          updatedImages[imageKey] = null;
          updatedImages[descriptionKey] = image?.description || "";
        }
      }

      return updatedImages;
    },
    [originalImagesRef, imageQueueRef]
  );

  // Image Handlers
  const handleImageUpload = async (key, file, index = null) => {
    console.log(`📸 [Step2] handleImageUpload called:`, {
      key,
      fileType: file === null ? 'null' : file instanceof File ? 'File' : typeof file,
      index
    });
    
    try {
      if (!file) {
        console.log(`🗑️ [Step2] Deleting image for key: ${key}`);
        
        imageQueueRef.current.delete(key);
        originalImagesRef.current.delete(key);
        clearImage(key);
        handleInputChange(key, null);
        
        setImageErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[key];
          return newErrors;
        });
        
        clearFieldError(key);
        
        setFormValues(prev => ({
          ...prev,
          [key]: null
        }));
        
        console.log(`✅ [Step2] Image deletion complete for ${key}`);
        return;
      }

      if (!(file instanceof File)) {
        if (typeof file === "string") {
          handleInputChange(key, file);
          return;
        }
        handleInputChange(key, null);
        imageQueueRef.current.delete(key);
        originalImagesRef.current.delete(key);
        clearImage(key);
        clearFieldError(key);
        return;
      }

      setImageErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });

      originalImagesRef.current.set(key, file);
      imageQueueRef.current.set(key, file);
      handleInputChange(key, file);
    } catch (error) {
      console.error(`❌ [Step2] Error in handleImageUpload:`, error);
    }
  };

  const handleImageDescriptionChange = (key, value) => {
    handleInputChange(key, value);
  };

  // Form Handlers
  const handleInputChange = (key, value) => {
    try {
      const errors = validateField(key, value);
      if (!errors?.[key]) {
        clearFieldError(key);
      }

      setFormValues((prev) => ({
        ...(prev || {}),
        [key]: value,
      }));
    } catch (error) {
      console.warn(`Error handling input change for ${key}:`, error);
    }
  };

  // Service Handlers
  const toggleServiceSelection = (serviceId) => {
    setFormValues((prevFormValues) => {
      try {
        const prevServices = prevFormValues?.services || [];
        const updatedServices = prevServices.map((service) => {
          if (service?.id === serviceId) {
            return {
              ...(service || {}),
              available: !service?.available,
            };
          }
          return service;
        });

        return {
          ...(prevFormValues || {}),
          services: updatedServices,
        };
      } catch (error) {
        console.warn("Error toggling service selection:", error);
        return prevFormValues;
      }
    });
  };

  const handleServiceChange = (serviceId, updates) => {
    setFormValues((prevFormValues) => {
      try {
        const prevServices = prevFormValues?.services || [];
        const updatedServices = prevServices.map((service) => {
          if (service?.id === serviceId) {
            return { ...(service || {}), ...(updates || {}) };
          }
          return service;
        });

        return {
          ...(prevFormValues || {}),
          services: updatedServices,
        };
      } catch (error) {
        console.warn("Error handling service change:", error);
        return prevFormValues;
      }
    });
  };

  const handleLocationTypeChange = (selectedValue, serviceId) => {
    if (selectedValue === "included") {
      handleServiceChange(serviceId, {
        location_type: selectedValue,
        price: 0,
      });
    } else {
      handleServiceChange(serviceId, { location_type: selectedValue });
    }
  };

  const handlePriceChange = (value, serviceId) => {
    const numericValue = value === "" || value === null || value === undefined ? null : parseFloat(value);
    handleServiceChange(serviceId, { price: numericValue });
  };

  const handleIndicativePriceChange = (value, serviceId) => {
    const numericValue = value === "" || value === null || value === undefined ? null : parseFloat(value);
    handleServiceChange(serviceId, { indicative_price: numericValue });
  };

  const handleDescriptionChange = (value, serviceId) => {
    handleServiceChange(serviceId, { description: value });
  };

  // Seminar Handlers
  const handleServiceOfferChange = (e, index) => {
    try {
      setFormValues((prevValues) => ({
        ...(prevValues || {}),
        seminars: (prevValues?.seminars || []).map((seminar, i) =>
          i === index ? { ...(seminar || {}), offer: e?.target?.value || "" } : seminar
        ),
      }));

      if (fieldErrors?.seminars) {
        clearFieldError("seminars");
      }
    } catch (error) {
      console.warn("Error handling service offer change:", error);
    }
  };

  const handleServicePriceChange = (value, index) => {
    try {
      const numericValue = value === "" || value === null || value === undefined ? null : parseFloat(value);

      setFormValues((prevValues) => ({
        ...(prevValues || {}),
        seminars: (prevValues?.seminars || []).map((seminar, i) =>
          i === index ? { ...(seminar || {}), price: numericValue } : seminar
        ),
      }));
    } catch (error) {
      console.warn("Error handling service price change:", error);
    }
  };

  // Custom Service Handlers
  const handleNewServiceNameChange = (value, index) => {
    try {
      setFormValues((prevValues) => {
        const currentCustomServices = prevValues?.custom_services || [
          { name: "", price: "" },
          { name: "", price: "" },
        ];

        const updatedCustomServices = [...currentCustomServices];

        while (updatedCustomServices.length <= index) {
          updatedCustomServices.push({ name: "", price: "" });
        }

        updatedCustomServices[index] = {
          ...(updatedCustomServices[index] || {}),
          name: value || "",
        };

        return {
          ...(prevValues || {}),
          custom_services: updatedCustomServices,
        };
      });
    } catch (error) {
      console.warn("Error handling new service name change:", error);
    }
  };

  const handleNewServicePriceChange = (value, index) => {
    try {
      const numericValue = value === "" || value === null || value === undefined ? "" : parseFloat(value) || 0;

      setFormValues((prevValues) => {
        const currentCustomServices = prevValues?.custom_services || [
          { name: "", price: "" },
          { name: "", price: "" },
        ];

        const updatedCustomServices = [...currentCustomServices];

        while (updatedCustomServices.length <= index) {
          updatedCustomServices.push({ name: "", price: "" });
        }

        updatedCustomServices[index] = {
          ...(updatedCustomServices[index] || {}),
          price: numericValue,
        };

        return {
          ...(prevValues || {}),
          custom_services: updatedCustomServices,
        };
      });
    } catch (error) {
      console.warn("Error handling new service price change:", error);
    }
  };

  // ============ EDIT CUSTOM SERVICE HANDLER (PUT) ============
  const handleUpdateCustomService = useCallback(async (serviceId, updates) => {
    console.log('✏️ Updating custom service:', serviceId, updates);
    
    setIsUpdatingService(true);
    try {
      await updatePlaceService(serviceId, updates, accessToken, language);
      
      // Update local state
      setFormValues((prev) => ({
        ...prev,
        services: prev.services.map(service =>
          service.id === serviceId 
            ? { ...service, ...updates }
            : service
        )
      }));
      
      // Show success message
      setServerErrors([{
        code: 'update_success',
        message: t('services.updateSuccess') || 'Service updated successfully',
        type: 'success'
      }]);
      
      setTimeout(() => {
        setServerErrors(prev => prev.filter(err => err.code !== 'update_success'));
      }, 3000);
      
      console.log('✅ Service updated successfully');
    } catch (error) {
      console.error('❌ Failed to update service:', error);
      setServerErrors([{
        code: 'update_failed',
        message: error.message || t('services.updateFailed') || 'Failed to update service',
        type: 'error'
      }]);
    } finally {
      setIsUpdatingService(false);
    }
  }, [accessToken, language, t]);

  // ============ DELETE CUSTOM SERVICE HANDLER (DELETE) ============
  const handleDeleteCustomService = useCallback(async (serviceId) => {
    console.log('🗑️ Deleting custom service:', serviceId);
    
    setIsDeletingService(true);
    try {
      await deletePlaceService(serviceId, accessToken, language);
      
      // Remove from local state
      setFormValues((prev) => ({
        ...prev,
        services: prev.services.filter(service => service.id !== serviceId),
        custom_services: prev.custom_services.filter(s => s.id !== serviceId)
      }));
      
      // Show success message
      setServerErrors([{
        code: 'delete_success',
        message: t('services.deleteSuccess') || 'Service deleted successfully',
        type: 'success'
      }]);
      
      setTimeout(() => {
        setServerErrors(prev => prev.filter(err => err.code !== 'delete_success'));
      }, 3000);
      
      console.log('✅ Service deleted successfully');
    } catch (error) {
      console.error('❌ Failed to delete service:', error);
      setServerErrors([{
        code: 'delete_failed',
        message: error.message || t('services.deleteFailed') || 'Failed to delete service',
        type: 'error'
      }]);
    } finally {
      setIsDeletingService(false);
    }
  }, [accessToken, language, t]);

  // Check for critical image errors
  const hasCriticalImageErrors = useCallback((errors) => {
    return Object.values(errors).some(
      err => typeof err === 'string' && (
        err.toLowerCase().includes('too large') || 
        err.toLowerCase().includes('failed') ||
        err.toLowerCase().includes('error')
      )
    );
  }, []);

  // Submission handler
  const handleSubmit = async (direction) => {
    console.log("🚀 handleSubmit START", { direction, submitting, loading });
    
    if (direction === "prev") {
      console.log("👈 Navigating to previous step");
      navigate(`/create-space/step1/${spaceId}`);
      return;
    }

    console.log("✅ Running validation...");
    const isValid = validateAllRequiredFields();
    console.log("📊 Validation result:", isValid, "Errors:", fieldErrors);
    
    if (!isValid) {
      console.log("❌ Validation failed, scrolling to error");
      setTimeout(() => scrollToError(), 100);
      return;
    }

    console.log("🎯 Setting submitting=true");
    setSubmitting(true);
    setServerErrors([]);
    setImageErrors({});

    try {
      console.log("🖼️ Starting image compression...");
      console.log("📸 Original images:", originalImagesRef.current.size);
      console.log("📸 Image queue before:", imageQueueRef.current.size);
      
      const compressionResult = await compressAllImages(
        setCompressingImages, 
        setServerErrors, 
        setImageErrors,
        t
      );
      
      console.log("📦 Compression result:", compressionResult);
      console.log("📸 Image queue after:", imageQueueRef.current.size);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const currentImageErrors = imageErrorsRef.current;
      console.log("⚠️ Current image errors:", currentImageErrors);
      
      if (Object.keys(currentImageErrors).length > 0) {
        console.log("⚠️ Image compression errors detected:", currentImageErrors);
        
        if (hasCriticalImageErrors(currentImageErrors)) {
          console.log("❌ Critical image errors, stopping submission");
          setServerErrors(prev => [
            ...prev,
            {
              code: 'critical_image_errors',
              message: t('validation.criticalImageErrors') || 
                'Some images could not be properly compressed. Please remove or replace them.',
              type: 'error'
            }
          ]);
          setSubmitting(false);
          return;
        }
        
        setServerErrors(prev => [
          ...prev,
          {
            code: 'image_warnings',
            message: t('validation.imageWarnings') || 
              'Some images have warnings but submission can continue',
            type: 'warning'
          }
        ]);
      }
      
      console.log("📏 Calculating total payload size...");
      const finalSizeMB = await calculateTotalPayloadSize();
      console.log(`📊 Final payload size: ${finalSizeMB.toFixed(2)}MB / ${MAX_TOTAL_PAYLOAD_MB}MB`);
      
      if (finalSizeMB > MAX_TOTAL_PAYLOAD_MB) {
        console.log("❌ Payload too large, stopping");
        setServerErrors((prev) => [
          ...prev,
          {
            message: t("validation.payloadStillTooLarge", {
              current: finalSizeMB.toFixed(2),
              max: MAX_TOTAL_PAYLOAD_MB,
            }) || `Payload too large (${finalSizeMB.toFixed(2)}MB). Please remove some images.`,
            type: "error",
          },
        ]);
        setSubmitting(false);
        return;
      }

      console.log("📝 Preparing submission data...");
      console.log("📋 Form values:", {
        id: formValues?.id,
        place_id: formValues?.place_id,
        servicesCount: formValues?.services?.length,
        customServicesCount: formValues?.custom_services?.length,
        seminarsCount: formValues?.seminars?.length,
      });
      
      const submissionData = prepareSubmissionData(formValues, imageQueueRef);
      console.log("✅ Submission data prepared:", {
        keys: Object.keys(submissionData),
        hasId: !!submissionData.id,
        hasPlaceId: !!submissionData.place_id,
        servicesCount: submissionData.services?.length,
        imageFiles: Object.keys(submissionData).filter(k => k.startsWith('image_') && submissionData[k] instanceof File).length,
      });

      if (!submissionData.id && !submissionData.place_id) {
        console.error("❌ CRITICAL: No ID found in submission data!");
        throw new Error("Missing place ID");
      }

      setLoading(true);
      console.log("📡 Calling API with ID:", submissionData.id || submissionData.place_id);

      const response = await submitStep2(
        submissionData, 
        submissionData.id || submissionData.place_id, 
        language, 
        accessToken
      );
      
      console.log("📨 API Response received:", response);

      if (response && response.success && isMounted.current) {
        console.log("✅ Submission successful, navigating to step 3");
        navigate(`/create-space/step3/${spaceId}`);
      } else {
        console.log("❌ API response indicates failure:", response);
        throw new Error(t("errors.submissionFailed") || "Submission failed");
      }
    } catch (error) {
      console.error("💥 Submission error caught:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      if (!isMounted.current) {
        console.log("🔇 Component unmounted, ignoring error");
        return;
      }

      setServerErrors([]);

      if (error.response?.data) {
        const errorData = error.response.data;
        console.log("📢 Error response data:", errorData);
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          console.log("📢 Setting structured server errors:", errorData.errors);
          setServerErrors(errorData.errors);
        } 
        else if (errorData.code && errorData.message) {
          setServerErrors([{
            code: errorData.code,
            message: errorData.message,
            type: "error"
          }]);
        }
        else if (errorData.message) {
          setServerErrors([{
            code: "general_error",
            message: errorData.message,
            type: "error"
          }]);
        }
        else {
          setServerErrors([{
            code: "unknown_error",
            message: t("errors.generic") || "An error occurred. Please try again.",
            type: "error"
          }]);
        }
      } 
      else if (error.response?.status === 413) {
        setServerErrors([{
          code: "payload_too_large",
          message: t("validation.payloadTooLargeFinal") || 
            `The data is still too large after compression. Please try with fewer or smaller images.`,
          type: "error"
        }]);
      } 
      else if (error.message) {
        setServerErrors([{
          code: "general_error",
          message: error.message,
          type: "error"
        }]);
      } 
      else {
        setServerErrors([{
          code: "unknown_error",
          message: t("errors.generic") || "An error occurred. Please try again.",
          type: "error"
        }]);
      }
      
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      console.log("🏁 Finally block - resetting states");
      if (isMounted.current) {
        setLoading(false);
        setSubmitting(false);
      }
      console.log("🚀 handleSubmit END");
    }
  };

  // Render
  return (
    <div className={`${mainStyles.form} ${(loading || submitting) && mainStyles.loading}`}>
      {/* Navigation Button */}
      <PrevNextButton
        color="nav"
        onNext={() => handleSubmit("next")}
        onPrev={() => handleSubmit("prev")}
        isIconOnly
        disabled={loading || submitting}
      />

      {/* Area Section */}
      <AreaSection
        formValues={formValues || {}}
        fieldErrors={fieldErrors || {}}
        handleInputChange={handleInputChange}
        requiresDispositionTypes={requiresDispositionTypes}
        requiresWorkPlaces={requiresWorkPlaces}
        t={t}
      />

      {/* Pictures Section */}
      <PicturesSection
        formValues={formValues || {}}
        fieldErrors={fieldErrors || {}}
        imageErrors={imageErrors || {}}
        hasExtraPicturesAccess={hasExtraPicturesAccess}
        compressingImages={compressingImages || {}}
        onImageChange={handleImageUpload}
        onDescriptionChange={handleImageDescriptionChange}
        t={t}
        serverErrors={serverErrors || []}
      />

      {/* Free Services Section */}
      <FreeServicesSection
        services={formValues?.services || []}
        fieldErrors={fieldErrors || {}}
        toggleServiceSelection={toggleServiceSelection}
        t={t}
      />

      {/* Reservable Services Section */}
      <ReservableServicesSection
        services={formValues?.services || []}
        fieldErrors={fieldErrors || {}}
        toggleServiceSelection={toggleServiceSelection}
        handlePriceChange={handlePriceChange}
        handleLocationTypeChange={handleLocationTypeChange}
        handleDescriptionChange={handleDescriptionChange}
        formValues={formValues || {}}
        t={t}
      />

      {/* Onsite Services Section */}
      <OnsiteServicesSection
        services={formValues?.services || []}
        fieldErrors={fieldErrors || {}}
        toggleServiceSelection={toggleServiceSelection}
        handlePriceChange={handlePriceChange}
        formValues={formValues || {}}
        t={t}
      />

      {/* Custom Services Section with Edit & Delete */}
      <CustomServicesSection
        services={formValues?.services || []}
        customServices={formValues?.custom_services || []}
        fieldErrors={fieldErrors || {}}
        toggleServiceSelection={toggleServiceSelection}
        handlePriceChange={handlePriceChange}
        handleNewServiceNameChange={handleNewServiceNameChange}
        handleNewServicePriceChange={handleNewServicePriceChange}
        handleUpdateCustomService={handleUpdateCustomService}
        handleDeleteCustomService={handleDeleteCustomService}
        isUpdating={isUpdatingService}
        isDeleting={isDeletingService}
        formValues={formValues || {}}
        t={t}
      />

      {/* Seminar/Teambuilding Section */}
      {requiresSeminarSection && requiresSeminarSection(formValues?.placeUsage?.key) && (
        <SeminarSection
          seminars={formValues?.seminars || []}
          fieldErrors={fieldErrors || {}}
          handleServiceOfferChange={handleServiceOfferChange}
          handleServicePriceChange={handleServicePriceChange}
          formValues={formValues || {}}
          t={t}
        />
      )}

      {/* Next/Previous Buttons Container */}
      <div className={mainStyles.nextBtnContainer}>
        <PrevNextButton
          color="nav"
          onNext={() => handleSubmit("next")}
          onPrev={() => handleSubmit("prev")}
          disabled={loading || submitting}
          loading={submitting}
        />
      </div>
    </div>
  );
};

export default Step2;